<template>
  <div v-if="visible" class="sc-mask" @touchmove.prevent>
    <div class="sc-box">
      <div class="sc-head">
        <div class="sc-title">{{ passed ? '验证成功' : '请拖动滑块完成拼图' }}</div>
        <div class="sc-close" @click.stop="onClose">×</div>
      </div>

      <!-- 拼图区域：程序化随机背景 + 半透明白色磨砂缺口（参考 vue-puzzle-vcode 的视觉风格） -->
      <div
        class="sc-puzzle"
        :class="{ shaking: failShake }"
        :style="{ width: width + 'px', height: height + 'px' }"
      >
        <canvas
          ref="scMain"
          class="sc-canvas"
          :style="{ width: width + 'px', height: height + 'px' }"
        />
        <!-- 拼块画布：经典拼图块（随机凹凸造型），内容为缺口处同位置背景裁切 -->
        <canvas
          v-if="!passed"
          ref="scPiece"
          class="sc-piece-canvas"
          :class="{ dragging: dragging }"
          :style="pieceCanvasStyle"
        />
        <div v-if="passed" class="sc-ok">✓</div>
      </div>

      <!-- 拖动条：白色轨道 + 蓝色进度 + 白色把手（vcode 风格） -->
      <div class="sc-bar">
        <div
          class="sc-track"
          :style="{ width: width + 'px' }"
          @mousedown.prevent="onStart"
          @touchstart.prevent="onStart"
        >
          <div class="sc-fill" :class="{ ok: passed }" :style="{ width: fillW + 'px' }" />
          <div class="sc-tip">{{ tipText }}</div>
          <div
            class="sc-handle"
            :class="{ dragging: dragging, ok: passed }"
            :style="{ transform: 'translateX(' + handleOffset + 'px)' }"
          >
            <div v-if="!passed" class="sc-bars">
              <div class="sc-bar-line" />
              <div class="sc-bar-line" />
              <div class="sc-bar-line" />
            </div>
            <div v-else class="sc-handle-icon">✓</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 滑动拼图验证组件（H5/Web 版，纯前端本地校验，零依赖）
 * 由 wmMiniApp 小程序版 slide-captcha.vue 移植而来，视觉与几何参考
 * 高赞开源库 vue-puzzle-vcode（github.com/javaLuo/vue-puzzle-vcode）：
 * - 拼块为参数化拼图造型：3e×3e 方体（宽高一致），四条边随机分配"半圆凸起/半圆凹口/平直"（每次挑战形状不同）
 * - 缺口 = 半透明白色磨砂 + 白色描边 + 轻微内阴影
 * - 背景默认为程序化随机生成（主色渐变 + 同色系装饰图形，每次挑战都不同）并整体压暗 35%，
 *   拼块保持原亮度，暗底亮块层次分明；也可传入图片用 cover 裁切作为底图
 * 通过后 $emit('success')，点击右上角关闭 $emit('close')。
 *
 * 与小程序版的差异（按 docs/slide-captcha.md 第五节移植）：
 * - uni.createSelectorQuery -> $refs 直接取 canvas DOM
 * - canvas.createImage -> new Image()（本地图用 require 打包）
 * - uni.getSystemInfoSync -> window.devicePixelRatio
 * - touch 事件 -> mouse（move/up 挂 document）+ touch 双支持
 * - rpx -> px（600rpx×300rpx 拼图区 -> 300px×150px，把手 88rpx -> 44px）
 */

function rand(min, max) {
  return min + Math.random() * (max - min)
}
function randInt(min, max) {
  return Math.floor(rand(min, max + 1))
}

/** 圆角矩形路径（兼容不支持 ctx.roundRect 的浏览器） */
function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

/**
 * 参数化拼图块路径（方体 3e×3e，宽高一致；四条边各有半圆特征，半径 e/2，跨度 e）：
 * f = { top, right, bottom, left }，每边 1=凸起、-1=凹口、0=平直。
 * 全部用显式 arc 画半圆，方向经几何验证（凸起朝体外、凹口朝体内）。
 * @param x,y 方体左上角（画布坐标）
 * @param e   单位长度（方体宽的 1/3）
 */
function traceBrickPath(ctx, x, y, e, f) {
  const PI = Math.PI
  ctx.beginPath()
  ctx.moveTo(x, y)
  // 上边（左→右），特征中心 x+1.5e
  if (f.top !== 0) {
    ctx.lineTo(x + e, y)
    ctx.arc(x + 1.5 * e, y, e / 2, PI, 0, f.top === 1 ? false : true)
    ctx.lineTo(x + 3 * e, y)
  } else {
    ctx.lineTo(x + 3 * e, y)
  }
  // 右边（上→下），特征中心 y+1.5e
  if (f.right !== 0) {
    ctx.lineTo(x + 3 * e, y + e)
    ctx.arc(x + 3 * e, y + 1.5 * e, e / 2, -PI / 2, PI / 2, f.right === 1 ? false : true)
    ctx.lineTo(x + 3 * e, y + 3 * e)
  } else {
    ctx.lineTo(x + 3 * e, y + 3 * e)
  }
  // 下边（右→左），特征中心 x+1.5e
  if (f.bottom !== 0) {
    ctx.lineTo(x + 2 * e, y + 3 * e)
    ctx.arc(x + 1.5 * e, y + 3 * e, e / 2, 0, PI, f.bottom === 1 ? false : true)
    ctx.lineTo(x, y + 3 * e)
  } else {
    ctx.lineTo(x, y + 3 * e)
  }
  // 左边（下→上），特征中心 y+1.5e
  if (f.left !== 0) {
    ctx.lineTo(x, y + 2 * e)
    ctx.arc(x, y + 1.5 * e, e / 2, PI / 2, -PI / 2, f.left === 1 ? false : true)
    ctx.lineTo(x, y)
  } else {
    ctx.lineTo(x, y)
  }
  ctx.closePath()
}

export default {
  name: 'SlideCaptcha',
  props: {
    visible: { type: Boolean, default: false },
    // 拼图底图：可选，本地图片路径（require）或 URL，支持数组随机取一张；
    // 不传（默认）时使用程序化生成的随机背景图，每次挑战都不同
    image: { type: [String, Array], default: null },
    // 拼图区域宽高（px）
    width: { type: Number, default: 300 },
    height: { type: Number, default: 150 },
    // 允许误差（px，画布坐标系），鼠标拖动建议 8
    tolerance: { type: Number, default: 8 },
  },
  data() {
    return {
      dpr: 2,
      W: 0, // 主画布实际宽高（px）
      H: 0,
      e: 0, // 拼块单位长度（px）
      shape: null, // 拼块形状 { top, right, bottom, left }: 1凸 -1凹 0平
      holeX: 0, // 缺口方体左上角（画布坐标）
      holeY: 0,
      offset: 0, // 拼块方体当前 x（画布坐标）
      offMin: 0, // 可拖动范围
      offMax: 0,
      pieceLeft: 0, // 拼块画布 CSS 定位（px）
      pieceTop: 0,
      pieceCssW: 0,
      pieceCssH: 0,
      dragging: false,
      passed: false,
      verifying: false, // 通过判定中（吸附动画期间禁止再拖）
      failShake: false,
      ready: false,
      tipText: '按住滑块，拖动完成拼图',
      startX: 0,
      baseOffset: 0,
      handlePx: 44, // 滑块把手实际宽度（px）
      pieceMargin: 4, // 拼块画布描边/投影留白
      bgParams: null,
      img: null,
      mainCanvas: null,
      mainCtx: null,
      pieceCanvas: null,
      pieceCtx: null,
      _failTimer: null,
      _passTimer: null,
      _emitTimer: null,
    }
  },
  computed: {
    pieceCanvasStyle() {
      if (!this.ready) {
        // 初始化完成前藏到画面外，避免拼块从左上角闪现
        return { left: '-9999px', top: '-9999px', width: '1px', height: '1px' }
      }
      return {
        left: this.pieceLeft + 'px',
        top: this.pieceTop + 'px',
        width: this.pieceCssW + 'px',
        height: this.pieceCssH + 'px',
      }
    },
    handleOffset() {
      const range = this.offMax - this.offMin
      if (!range || !this.W) return 0
      return ((this.offset - this.offMin) / range) * Math.max(this.W - this.handlePx, 0)
    },
    fillW() {
      return this.handleOffset + this.handlePx
    },
  },
  watch: {
    visible(v) {
      if (v) {
        this.reset()
        this.$nextTick(() => {
          this.initCanvas()
        })
      } else {
        this.clearTimers()
        this.removeDocListeners()
      }
    },
  },
  created() {
    try {
      this.dpr = window.devicePixelRatio || 2
    } catch (e) {
      console.log('slideCaptcha devicePixelRatio error ==', e)
      this.dpr = 2
    }
  },
  beforeDestroy() {
    this.clearTimers()
    this.removeDocListeners()
  },
  methods: {
    clearTimers() {
      if (this._failTimer) {
        clearTimeout(this._failTimer)
        this._failTimer = null
      }
      if (this._passTimer) {
        clearTimeout(this._passTimer)
        this._passTimer = null
      }
      if (this._emitTimer) {
        clearTimeout(this._emitTimer)
        this._emitTimer = null
      }
    },

    reset() {
      this.passed = false
      this.dragging = false
      this.verifying = false
      this.failShake = false
      this.ready = false
      this.offset = 0
      this.tipText = '按住滑块，拖动完成拼图'
      this.clearTimers()
    },

    // 兼容 mouse / touch 事件取 clientX
    getClientX(e) {
      if (e.touches && e.touches.length) return e.touches[0].clientX
      if (e.changedTouches && e.changedTouches.length) return e.changedTouches[0].clientX
      return e.clientX
    },

    removeDocListeners() {
      if (typeof document === 'undefined') return
      document.removeEventListener('mousemove', this.onDocMove)
      document.removeEventListener('mouseup', this.onDocEnd)
      document.removeEventListener('touchmove', this.onDocMove)
      document.removeEventListener('touchend', this.onDocEnd)
      document.removeEventListener('touchcancel', this.onDocEnd)
    },

    async initCanvas() {
      try {
        const mainCanvas = this.$refs.scMain
        if (!mainCanvas) throw new Error('main canvas not ready')
        this.mainCanvas = mainCanvas
        this.mainCtx = mainCanvas.getContext('2d')
        this.W = mainCanvas.clientWidth
        this.H = mainCanvas.clientHeight
        this.mainCanvas.width = this.W * this.dpr
        this.mainCanvas.height = this.H * this.dpr
        this.mainCtx.scale(this.dpr, this.dpr)

        await this.loadImage()

        const pieceCanvas = this.$refs.scPiece
        if (!pieceCanvas) throw new Error('piece canvas not ready')
        this.pieceCanvas = pieceCanvas
        this.pieceCtx = pieceCanvas.getContext('2d')

        this.genChallenge()
      } catch (e) {
        console.log('slideCaptcha initCanvas error ==', e)
        this.tipText = '验证组件加载失败，请关闭后重试'
      }
    },

    // 底图加载：不传 image 时直接使用程序化随机背景；传入图片加载失败时也退化到随机背景
    loadImage() {
      return new Promise((resolve) => {
        const srcs = Array.isArray(this.image)
          ? this.image.filter(Boolean)
          : this.image
          ? [this.image]
          : []
        if (!srcs.length) {
          this.img = null
          resolve()
          return
        }
        const src = srcs[randInt(0, srcs.length - 1)]
        const img = new Image()
        img.onload = () => {
          this.img = img
          resolve()
        }
        img.onerror = () => {
          console.log('slideCaptcha image load error ==', src)
          this.img = null
          resolve()
        }
        img.src = src
      })
    },

    // ---------- 背景生成与绘制 ----------

    // 程序化随机背景参数（无图时使用）：主色渐变 + 相邻色径向光晕 + 同色系装饰图形，
    // 每次挑战生成一张完全不同的"图"。所有随机值都在此处固定下来，
    // 主画布与拼块画布用同一份参数绘制，保证拼块内容与缺口严格对齐。
    genBgParams(W, H) {
      const hue = randInt(0, 360) // 主色相
      const hue2 = (hue + randInt(30, 80)) % 360 // 渐变第二色（相邻色相，整体协调不杂乱）
      const hue3 = (hue + randInt(-80, 80) + 360) % 360 // 光晕色
      const decors = []
      const randHue = (spread) => (hue + randInt(-spread, spread) + 360) % 360
      const mkColor = (h, alpha) =>
        'hsla(' + h + ',' + randInt(45, 75) + '%,' + randInt(45, 72) + '%,' + alpha + ')'

      // 柔和大圆
      for (let i = 0, n = randInt(4, 6); i < n; i++) {
        decors.push({
          type: 'circle',
          x: rand(0, W),
          y: rand(0, H),
          r: rand(16, Math.max(W, H) * 0.3),
          color: mkColor(randHue(45), rand(0.08, 0.2)),
        })
      }
      // 随机旋转的胶囊条
      for (let i = 0, n = randInt(2, 4); i < n; i++) {
        decors.push({
          type: 'pill',
          x: rand(0, W),
          y: rand(0, H),
          w: rand(36, W * 0.45),
          h: rand(8, 22),
          angle: rand(0, Math.PI),
          color: mkColor(randHue(45), rand(0.12, 0.25)),
        })
      }
      // 随机多边形（3~5 边形，顶点半径抖动）
      for (let i = 0, n = randInt(2, 3); i < n; i++) {
        const cx = rand(0, W)
        const cy = rand(0, H)
        const R = rand(18, Math.min(W, H) * 0.28)
        const sides = randInt(3, 5)
        const pts = []
        for (let s = 0; s < sides; s++) {
          const a = (Math.PI * 2 * s) / sides + rand(-0.4, 0.4)
          const rr = R * rand(0.7, 1.15)
          pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr])
        }
        decors.push({ type: 'poly', pts, color: mkColor(randHue(45), rand(0.1, 0.22)) })
      }
      // 小亮点
      for (let i = 0, n = randInt(6, 12); i < n; i++) {
        decors.push({
          type: 'dot',
          x: rand(0, W),
          y: rand(0, H),
          r: rand(1.5, 3.5),
          color: mkColor(randHue(30), rand(0.2, 0.45)),
        })
      }
      return {
        hue,
        hue2,
        hue3,
        glowX: rand(W * 0.2, W * 0.8),
        glowY: rand(H * 0.2, H * 0.8),
        glowR: rand(Math.max(W, H) * 0.35, Math.max(W, H) * 0.7),
        decors,
      }
    },

    drawBackground(ctx, W, H, bg, dim) {
      if (this.img) {
        // 图片按 cover 裁切铺满
        const iw = this.img.width
        const ih = this.img.height
        if (iw && ih) {
          const scale = Math.max(W / iw, H / ih)
          const dw = iw * scale
          const dh = ih * scale
          ctx.drawImage(this.img, (W - dw) / 2, (H - dh) / 2, dw, dh)
        } else {
          ctx.drawImage(this.img, 0, 0, W, H)
        }
      } else {
        // 程序化随机背景：主色渐变 + 相邻色光晕 + 装饰图形（全部参数来自 bgParams，可精确复现）
        const g = ctx.createLinearGradient(0, 0, W, H)
        g.addColorStop(0, 'hsl(' + bg.hue + ',55%,68%)')
        g.addColorStop(1, 'hsl(' + bg.hue2 + ',55%,58%)')
        ctx.fillStyle = g
        ctx.fillRect(0, 0, W, H)
        const rg = ctx.createRadialGradient(bg.glowX, bg.glowY, 0, bg.glowX, bg.glowY, bg.glowR)
        rg.addColorStop(0, 'hsla(' + bg.hue3 + ',60%,78%,0.4)')
        rg.addColorStop(1, 'hsla(' + bg.hue3 + ',60%,78%,0)')
        ctx.fillStyle = rg
        ctx.fillRect(0, 0, W, H)
        bg.decors.forEach((d) => {
          ctx.fillStyle = d.color
          if (d.type === 'circle' || d.type === 'dot') {
            ctx.beginPath()
            ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2)
            ctx.fill()
          } else if (d.type === 'pill') {
            ctx.save()
            ctx.translate(d.x, d.y)
            ctx.rotate(d.angle)
            roundRectPath(ctx, -d.w / 2, -d.h / 2, d.w, d.h, d.h / 2)
            ctx.fill()
            ctx.restore()
          } else if (d.type === 'poly') {
            ctx.beginPath()
            ctx.moveTo(d.pts[0][0], d.pts[0][1])
            for (let i = 1; i < d.pts.length; i++) ctx.lineTo(d.pts[i][0], d.pts[i][1])
            ctx.closePath()
            ctx.fill()
          }
        })
      }
      // dim=true 时整体压暗（主画布用）：暗底亮块，拼块与背景拉开层次
      if (dim) {
        ctx.fillStyle = 'rgba(0,0,0,0.35)'
        ctx.fillRect(0, 0, W, H)
      }
    },

    // ---------- 挑战生成与画布绘制 ----------

    // 随机拼块形状：1~2 对凸起+凹口随机落在四条边上，其余边平直
    genShape() {
      const sides = ['top', 'right', 'bottom', 'left']
      const n = randInt(1, 2) // 凸起/凹口对数
      const feats = []
      for (let i = 0; i < n; i++) feats.push(1)
      for (let i = 0; i < n; i++) feats.push(-1)
      while (feats.length < 4) feats.push(0)
      for (let i = feats.length - 1; i > 0; i--) {
        const j = randInt(0, i)
        const t = feats[i]
        feats[i] = feats[j]
        feats[j] = t
      }
      const f = {}
      sides.forEach((s, i) => {
        f[s] = feats[i]
      })
      return f
    },

    genChallenge() {
      const W = this.W
      const H = this.H
      if (W < 60 || H < 60) {
        throw new Error('canvas size invalid: ' + W + 'x' + H)
      }
      // 拼块单位长度：方体 3e×3e，宽高一致（310 宽画布上 e=15，方体 45×45）
      const e = Math.max(10, Math.min(18, Math.round((W * 15) / 310)))
      const shape = this.genShape()
      const leftB = shape.left === 1 ? e / 2 : 0
      const rightB = shape.right === 1 ? e / 2 : 0
      const topB = shape.top === 1 ? e / 2 : 0
      const bottomB = shape.bottom === 1 ? e / 2 : 0
      const pad = 6
      const offMin = leftB // 左凸时拼块起始位置让出凸起宽度
      const offMax = W - 3 * e - rightB - pad
      const minX = offMin + pad
      const maxX = offMax - pad
      const minY = topB + pad
      const maxY = H - 3 * e - bottomB - pad
      if (maxX - minX < 20 || maxY - minY < 10) {
        throw new Error('canvas too small for puzzle')
      }
      this.e = e
      this.shape = shape
      this.holeX = Math.round(rand(minX, maxX))
      this.holeY = Math.round(rand(minY, maxY))
      this.offMin = Math.round(offMin)
      this.offMax = Math.round(offMax)
      this.offset = this.offMin
      this.bgParams = this.genBgParams(W, H)
      this.drawMain(true)
      this.drawPiece()
      this.ready = true
    },

    drawMain(withHole) {
      const ctx = this.mainCtx
      if (!ctx || !this.bgParams || !this.e || !this.shape) return
      ctx.clearRect(0, 0, this.W, this.H)
      // 主画布背景压暗（dim），拼块保持原亮度，拉开层次
      this.drawBackground(ctx, this.W, this.H, this.bgParams, true)
      if (withHole) {
        // 缺口：白色磨砂 + 轻微内阴影 + 白描边（vcode 风格）
        ctx.save()
        traceBrickPath(ctx, this.holeX, this.holeY, this.e, this.shape)
        ctx.fillStyle = 'rgba(255,255,255,0.8)'
        ctx.fill()
        ctx.clip()
        ctx.shadowColor = 'rgba(0,0,0,0.3)'
        ctx.shadowBlur = 3
        ctx.strokeStyle = 'rgba(0,0,0,0.3)'
        ctx.lineWidth = 2
        ctx.stroke()
        ctx.restore()
        ctx.save()
        traceBrickPath(ctx, this.holeX, this.holeY, this.e, this.shape)
        ctx.strokeStyle = 'rgba(255,255,255,0.9)'
        ctx.lineWidth = 1
        ctx.stroke()
        ctx.restore()
      }
    },

    drawPiece() {
      const ctx = this.pieceCtx
      if (!ctx || !this.bgParams || !this.e || !this.shape) return
      const m = this.pieceMargin
      const e = this.e
      const shape = this.shape
      const leftB = shape.left === 1 ? e / 2 : 0
      const rightB = shape.right === 1 ? e / 2 : 0
      const topB = shape.top === 1 ? e / 2 : 0
      const bottomB = shape.bottom === 1 ? e / 2 : 0
      // 画布包围盒：方体 3e×3e + 各边凸起 + 留白
      const cw = 3 * e + leftB + rightB + m * 2
      const ch = 3 * e + topB + bottomB + m * 2
      // 重设 width 会清空画布并重置上下文变换，需重新 scale(dpr)
      this.pieceCanvas.width = cw * this.dpr
      this.pieceCanvas.height = ch * this.dpr
      ctx.scale(this.dpr, this.dpr)
      // 内容原点：方体左上角在画布 (leftB + m, topB + m)，
      // 故 translate = (m - holeX + leftB, m - holeY + topB)，bgX/bgY 不含 -m
      const bgX = this.holeX - leftB
      const bgY = this.holeY - topB
      // 拼块内容 = 缺口处同位置图片裁切（不压暗，保持亮色）
      ctx.save()
      ctx.translate(m - bgX, m - bgY)
      traceBrickPath(ctx, this.holeX, this.holeY, e, shape)
      ctx.clip()
      this.drawBackground(ctx, this.W, this.H, this.bgParams, false)
      ctx.restore()
      // 白描边 + 轻投影
      ctx.save()
      ctx.translate(m - bgX, m - bgY)
      traceBrickPath(ctx, this.holeX, this.holeY, e, shape)
      ctx.shadowColor = 'rgba(0,0,0,0.45)'
      ctx.shadowBlur = 4
      ctx.strokeStyle = 'rgba(255,255,255,0.95)'
      ctx.lineWidth = 1.2
      ctx.stroke()
      ctx.restore()
      // 更新 CSS 尺寸与定位（方体左上角跟随 offset，与缺口精确对齐）
      this.pieceCssW = cw
      this.pieceCssH = ch
      this.pieceTop = this.holeY - topB - m
      this.pieceLeft = this.offset - leftB - m
    },

    // ---------- 拖动与校验 ----------

    onClose() {
      if (this.dragging) return
      this.$emit('close')
    },

    // mousedown / touchstart：开始拖动，move/up 挂到 document 上
    onStart(e) {
      if (this.passed || this.verifying || this.dragging || !this.ready) return
      // 失败复位定时器未触发前再次拖动：取消复位
      if (this._failTimer) {
        clearTimeout(this._failTimer)
        this._failTimer = null
      }
      this.dragging = true
      this.failShake = false
      this.startX = this.getClientX(e)
      this.baseOffset = this.offset
      document.addEventListener('mousemove', this.onDocMove)
      document.addEventListener('mouseup', this.onDocEnd)
      document.addEventListener('touchmove', this.onDocMove, { passive: false })
      document.addEventListener('touchend', this.onDocEnd)
      document.addEventListener('touchcancel', this.onDocEnd)
    },
    onDocMove(e) {
      if (!this.dragging || this.passed) return
      if (e.cancelable) e.preventDefault()
      const dx = this.getClientX(e) - this.startX
      let next = this.baseOffset + dx
      if (next < this.offMin) next = this.offMin
      if (next > this.offMax) next = this.offMax
      this.offset = next
      const leftB = this.shape && this.shape.left === 1 ? this.e / 2 : 0
      this.pieceLeft = next - leftB - this.pieceMargin
    },
    onDocEnd() {
      if (!this.dragging) return
      this.dragging = false
      this.removeDocListeners()
      if (Math.abs(this.offset - this.holeX) <= this.tolerance) {
        // 通过：拼块吸附缺口 -> 补全缺口 -> 通知父组件
        this.verifying = true
        this.offset = this.holeX
        const leftB = this.shape && this.shape.left === 1 ? this.e / 2 : 0
        this.pieceLeft = this.holeX - leftB - this.pieceMargin
        this.tipText = '验证通过'
        this._passTimer = setTimeout(() => {
          this.passed = true
          this.drawMain(false)
          this._emitTimer = setTimeout(() => {
            this.$emit('success')
          }, 300)
        }, 260)
      } else {
        // 失败：抖动提示，随后复位并重新生成挑战
        this.failShake = true
        this.tipText = '验证失败，请重试'
        this._failTimer = setTimeout(() => {
          this.offset = this.offMin
          this.failShake = false
          this.tipText = '按住滑块，拖动完成拼图'
          this.genChallenge()
        }, 500)
      }
    },
  },
}
</script>

<style scoped>
.sc-mask {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.sc-box {
  width: 332px;
  background-color: #ffffff;
  border-radius: 6px;
  padding: 16px;
  box-sizing: border-box;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
}

.sc-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.sc-title {
  color: #3c3c3c;
  font-size: 15px;
  font-weight: 500;
}

.sc-close {
  width: 24px;
  height: 24px;
  line-height: 22px;
  text-align: center;
  color: #999999;
  font-size: 22px;
  cursor: pointer;
  -webkit-user-select: none;
  user-select: none;
}

.sc-puzzle {
  position: relative;
  overflow: hidden;
  background-color: #f2f3f5;
  border-radius: 4px;
}

.sc-canvas {
  display: block;
}

.sc-piece-canvas {
  position: absolute;
  transition: left 0.18s ease;
}

.sc-piece-canvas.dragging {
  transition: none;
}

.shaking {
  animation: scShake 0.3s;
}

@keyframes scShake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-4px);
  }
  75% {
    transform: translateX(4px);
  }
}

.sc-ok {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #07c160;
  font-size: 60px;
  font-weight: 600;
  z-index: 3;
}

.sc-bar {
  margin-top: 14px;
}

/* vcode 风格拖动条：白色轨道 + 蓝色进度 + 白色把手 */
.sc-track {
  position: relative;
  height: 44px;
  background-color: #eef1f8;
  border-radius: 4px;
  display: flex;
  align-items: center;
  overflow: hidden;
  box-shadow: 0 0 4px rgba(240, 240, 240, 0.6) inset;
  cursor: pointer;
  -webkit-user-select: none;
  user-select: none;
}

.sc-fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(1, 173, 231, 0.35);
  transition: none;
}

.sc-fill.ok {
  background-color: rgba(7, 193, 96, 0.35);
}

.sc-tip {
  position: absolute;
  left: 0;
  right: 0;
  text-align: center;
  color: #9aa0ae;
  font-size: 13px;
}

.sc-handle {
  position: absolute;
  left: 0;
  top: 0;
  width: 44px;
  height: 44px;
  background-color: #ffffff;
  border-radius: 4px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform 0.18s ease;
}

.sc-handle.ok {
  background-color: #07c160;
}

.sc-handle.dragging {
  transition: none;
}

/* 把手三条竖杠图标 */
.sc-bars {
  display: flex;
  align-items: center;
  height: 40%;
}

.sc-bar-line {
  width: 2px;
  height: 100%;
  background-color: #01ade7;
  border-radius: 1px;
  margin: 0 2px;
}

.sc-handle-icon {
  color: #ffffff;
  font-size: 20px;
}
</style>
